import * as React from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useContext, useEffect } from 'react';

import { CursorContext, CursorTypes } from '@providers/CursorProvider';
import { ColorThemeContext } from '@providers/ColorThemeContext';

const svgAppearanceTransition = 1;
const appearanceTransition = 2.5;

type ClewProps = {
  isMouseOvered: boolean
  delay: number
  className?: string
};

export const Clew: React.FC<ClewProps> = ({
  isMouseOvered,
  delay,
  className,
}) => {
  const { toggleCursorType } = useContext(CursorContext);
  const { setColorThemeMode } = useContext(ColorThemeContext);
  const controls = useAnimation();
  const { themeColors } = useContext(ColorThemeContext);

  useEffect(() => {
    if (!isMouseOvered) {
      controls.start({
        rotate: [
          0,
          30,
          -25,
          20,
          -15,
          10,
          -5,
          0,
        ],
      });
    } else {
      controls.stop();
      controls.set({
        rotate: 0,
      });
    }
  }, [controls, isMouseOvered]);

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      transition={{
        duration: .3,
        delay: .3,
      }}
      onMouseEnter={() => toggleCursorType(CursorTypes.pointer)}
      onMouseLeave={() => toggleCursorType(CursorTypes.default)}
      onClick={setColorThemeMode}
    >
      <motion.svg
        className={className}
        width="97"
        height="237"
        viewBox="0 0 97 237"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ originX: '50%', originY: '0' }}
        initial={{ rotate: 0 }}
        animate={controls}
        // Whole Rotation animation
        transition={{
          ease: [0.6, 0.01, -0.05, 0.95],
          duration: 6,
          delay: delay + svgAppearanceTransition + appearanceTransition + 3,
          repeat: Infinity,
          repeatDelay: 11.5 - 2.5,
        }}
      >
        <motion.g
          initial={{ y: -300 }}
          animate={{ y: 0 }}
          // Appearance of clew
          transition={{
            ease: [0.6, 0.01, -0.05, 0.95],
            duration: svgAppearanceTransition,
            delay: delay,
          }}
        >
          <motion.path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M88.8783 200.053C88.3796 201.726 87.7797 203.356 87.0857 204.935C84.333 204.344 80.3628 202.606 76.1898 199.994C72.4038 197.624 68.7013 194.692 65.8502 191.615C67.5763 190.352 69.3908 189.186 71.2916 188.138C74.0535 190.584 77.4172 193.299 80.8951 195.634C83.4325 197.338 86.1743 198.939 88.8783 200.053ZM80.5226 215.375C81.7809 213.904 82.9396 212.346 83.9884 210.71C80.5443 209.684 76.5873 207.76 72.8091 205.395C68.577 202.747 64.2988 199.376 60.9335 195.682C59.608 196.914 58.349 198.203 57.1575 199.535C64.7452 207.279 71.4677 211.469 80.5226 215.375ZM75.6738 220.193C67.2755 216.351 60.5078 211.94 53.1377 204.533C51.9038 206.241 50.7799 207.977 49.766 209.718C55.828 214.856 63.8513 220.609 69.987 224.244C71.9964 223.05 73.898 221.694 75.6738 220.193ZM63.156 227.508C57.7972 224.063 51.7691 219.661 46.7824 215.531C45.9459 217.406 45.2402 219.253 44.6655 221.042C48.9352 224.737 51.324 226.78 55.8992 229.48C58.4014 229.037 60.8273 228.373 63.156 227.508ZM43.0334 228.044C43.8528 228.738 44.6391 229.39 45.4296 230.018C44.5624 229.955 43.7025 229.866 42.8507 229.752C42.8934 229.198 42.9543 228.628 43.0334 228.044ZM84.3347 183.295C85.8274 184.825 87.0042 185.99 88.234 186.909C89.0027 187.483 89.7753 187.952 90.6261 188.38C90.6101 190.181 90.4812 191.955 90.246 193.696C88.4543 192.859 86.4898 191.715 84.4474 190.344C82.091 188.761 79.7642 186.969 77.662 185.213C79.7984 184.42 82.0233 183.773 84.3347 183.295ZM88.9472 176.181C72.2827 177.682 59.2576 186.775 50.4116 197.625C42.3841 207.47 37.5183 219.111 36.5732 228.416C35.2318 228.02 33.9183 227.56 32.6363 227.039C32.5606 226.047 32.6022 224.808 32.8114 223.321C33.1463 220.939 33.8814 218.126 35.0255 215.039C37.314 208.864 41.1555 201.816 46.3259 195.253C56.2496 182.658 70.607 172.378 87.7467 172.657C88.1969 173.808 88.5979 174.983 88.9472 176.181ZM14.9658 213.503C15.8523 214.667 16.7979 215.783 17.7983 216.847C18.4687 204.832 24.852 191.077 34.7153 179.969C45.2197 168.14 60.1336 158.773 77.5069 157.449C76.4635 156.458 75.3695 155.52 74.229 154.639L74.2234 154.714C56.0553 153.354 40.3041 164.67 29.3716 178.663C23.9464 185.606 19.8547 193.038 17.3817 199.504C16.1452 202.738 15.336 205.671 14.9515 208.136C14.5604 210.645 14.6478 212.424 14.9658 213.503ZM39.48 184.201C28.4535 196.618 22.861 211.738 24.3512 222.524C25 222.978 25.6621 223.415 26.337 223.833C26.38 223.37 26.4351 222.903 26.5012 222.433C26.9139 219.499 27.7865 216.235 29.0503 212.825C31.5777 206.005 35.7528 198.376 41.3205 191.31C51.3261 178.61 66.2408 167.254 84.6391 166.338C84.0922 165.427 83.5119 164.539 82.8999 163.676C65.4915 163.267 50.1952 172.134 39.48 184.201ZM51.8991 146.007C50.7779 145.918 49.6443 145.872 48.5 145.872C47.5925 145.872 46.6916 145.901 45.7984 145.957C46.9356 148.674 48.3138 151.252 49.6007 153.427C51.2817 152.603 53.005 151.861 54.7688 151.214C53.8034 149.732 52.7996 148.05 51.8991 146.007ZM63.9684 148.803C62.651 148.282 61.3012 147.827 59.9228 147.439C60.2479 147.94 60.5836 148.437 60.9297 148.95L60.9304 148.951L60.9309 148.952L60.934 148.957C61.0173 149.08 61.1013 149.205 61.186 149.33C62.1042 149.128 63.0318 148.951 63.9684 148.803ZM39.3047 146.879C37.3914 147.305 35.5272 147.861 33.7218 148.537C35.0973 152.587 37.1088 156.695 39.3488 159.789C40.8641 158.642 42.428 157.555 44.0384 156.54C42.4422 153.831 40.672 150.467 39.3047 146.879ZM34.4337 163.861C31.7655 160.268 29.4944 155.701 27.9141 151.236C26.2072 152.194 24.5737 153.267 23.0243 154.445C24.4078 158.976 26.8101 163.543 30.2334 167.94C31.5769 166.539 32.9778 165.175 34.4337 163.861ZM7.77795 177.168C8.35526 174.992 9.10235 172.885 10.0042 170.862C12.1263 175.273 15.0914 179.601 18.2952 183.465C17.0934 185.423 15.9908 187.382 14.9928 189.316C11.9124 185.406 9.64014 181.786 7.77795 177.168ZM6.37294 188.242C6.40357 193.685 7.4664 198.883 9.37562 203.652C9.88857 201.608 10.5797 199.451 11.4299 197.228C11.6168 196.739 11.8118 196.246 12.0148 195.749C9.89235 193.217 8.0265 190.8 6.37294 188.242ZM21.9896 177.896C18.3546 173.261 15.4276 168.314 14.0233 163.784C15.195 162.119 16.4842 160.543 17.8789 159.067C19.686 163.793 22.3958 168.41 25.9333 172.77C25.3936 173.423 24.8658 174.079 24.3502 174.739C23.5357 175.782 22.7485 176.835 21.9896 177.896ZM97 188C97 214.786 75.2858 236.5 48.5 236.5C21.7142 236.5 0 214.786 0 188C0 161.214 21.7142 139.5 48.5 139.5C75.2858 139.5 97 161.214 97 188Z"
            fill="#1F1F1F"

            initial={{ y: 0, rotate: -10 }}
            animate={{
              y: [
                0,
                -31.5,
                0,
                -15,
                0,
                -7,
                0,
              ],
              rotate: [
                0,
                -10,
                0,
                8,
                0,
                -4,
                0,
              ],
            }}
            // Animation of jumping ball
            transition={{
              ease: [0.6, 0.01, -0.05, 0.95],
              duration: appearanceTransition,
              delay: delay + svgAppearanceTransition,
            }}
          />
          <motion.path
            className="svgClew"
            d="M49 143C49.0004 129 49.0004 100.2 49.0004 87C49.0004 70.5 49.0006 49.1377 49.0004 30C49.0002 17 49.0004 5 49.0004 0"
            stroke={themeColors.primary}
            strokeWidth="5"

            initial={{
              d: 'M49 143C49.0004 129 49.0004 100.2 49.0004 87C49.0004 70.5 49.0006 49.1377 49.0004 30C49.0002 17 49.0004 5 49.0004 0',
            }}
            animate={{
              d: [
                'M49 143C49.0004 129 49.0004 100.2 49.0004 87C49.0004 70.5 49.0006 49.1377 49.0004 30C49.0002 17 49.0004 5 49.0004 0',
                'M48.9993 111.5C53.666 104.5 62.9993 87.2 62.9993 74C62.9993 57.5 22.4993 50 28.9993 32C34.1993 17.6 48.9993 5 48.9993 0',
                'M49 143C49.0004 129 49.0004 100.2 49.0004 87C49.0004 70.5 49.0006 49.1377 49.0004 30C49.0002 17 49.0004 5 49.0004 0',
                'M48.0001 128.5C43.3334 121.5 37 105.2 37 92C37 75.5 59.9988 71.1377 60 52C60.0009 37 48.0001 5.5 48.0001 0.5',
                'M49 143C49.0004 129 49.0004 100.2 49.0004 87C49.0004 70.5 49.0006 49.1377 49.0004 30C49.0002 17 49.0004 5 49.0004 0',
                'M48.9999 136.5C53.6666 129.5 55 119.7 55 106.5C55 90 42.0012 78.1377 42 59C41.9991 44 48.9999 5.5 48.9999 0.5',
                'M49 143C49.0004 129 49.0004 100.2 49.0004 87C49.0004 70.5 49.0006 49.1377 49.0004 30C49.0002 17 49.0004 5 49.0004 0',
              ],
            }}
            // Animation of jumping border
            transition={{
              ease: [0.6, 0.01, -0.05, 0.95],
              duration: appearanceTransition,
              delay: delay + svgAppearanceTransition,
            }}
          />
        </motion.g>
      </motion.svg>
    </motion.div>
  );
};